<template>
  <b-card
    body-class="p-50"
    class="mb-50 mb-md-1 border-info-2"
  >
    <div class="d-flex flex-column flex-md-row justify-content-md-between mb-1">
      <div class="bg-blue-gradient d-flex align-items-center mb-25 mb-md-0">
        <div class="d-flex-center">
          <b-alert
            show
            variant="primary"
            class="p-75 p-md-1 mr-25 mr-md-75 mb-0"
            style="width: fit-content"
          >
            <feather-icon
              icon="StarIcon"
              :size="isMobileView ? '20' : '30'"
              class="text-body"
            />
          </b-alert>
        </div>

        <div>
          <div :class="`text-airline fw-700 mb-0 mb-md-25 ${isMobileView ? 'font-medium-4' : 'font-medium-3'}`">
            {{ $t('flight.addonsBundle') }}
            <feather-icon
              v-if="isMobileView"
              id="header-tooltip-mobile-bundle"
              icon="InfoIcon"
              size="16"
              class="text-info cursor-pointer"
            />
          </div>

          <b-tooltip
            v-if="isMobileView"
            target="header-tooltip-mobile-bundle"
            triggers="hover focus"
            boundary="window"
            variant="info"
          >
            {{ $t('flight.bundleDescription') }}
          </b-tooltip>

          <span
            v-if="!isMobileView"
            class="fw-600"
          >
            {{ $t('flight.bundleDescription') }}
          </span>
        </div>
      </div>
    </div>

    <!-- ANCHOR - BODY -->
    <IAmOverlay
      :loading="loadingAncillary"
      :spinner-variant="'info'"
    >
      <AppCollapse type="border card">
        <AppCollapseItem
          v-for="(trip, indexTrip) in tripDataLists"
          :key="indexTrip"
          title
          isVisible
          class-header="card-header bg-light-secondary"
          class-body="d-flex flex-wrap justify-content-center justify-content-md-start p-50"
          card-class="mb-50"
        >
          <template #header>
            <span class="text-dark fw-700">
              {{ $t('flight.itinerary') }} {{ indexTrip + 1 }}: {{ getShortNameTripByMultiSegments(trip.segments) }}
            </span>
          </template>

          <div
            v-if="!loadingAncillary && isEmpty(trip.bundles)"
            class="font-italic text-warning"
          >
            {{ $t('flight.noBundle') }}
          </div>

          <b-card
            v-for="bundle in trip.bundles"
            :key="bundle.code"
            class="border-secondary mx-25 mx-md-50 mb-50 mb-md-25"
            body-class="p-25 p-md-50"
            footer-class="p-0"
            :style="isMobileView ? 'width: 100%' : 'min-width: 300px'"
          >
            <div class="border-bottom pb-25 mb-50">
              <div class="d-flex-between text-warning">
                <span class="fw-700 font-medium-2">{{ bundle.key }}</span>
                <small>{{ bundle.code }}</small>
              </div>
              <div class="">
                <span class="fw-700 text-warning">{{ formatCurrency(bundle?.fares?.[0]?.total || NaN) }} VND</span> /<span class="fw-600 text-small font-italic">hành khách</span>
              </div>
            </div>

            <div class="font-weight-bolder">
              {{ $t('flight.bundleIncludesPax') }}:
            </div>

            <div
              v-for="bundleItem in bundleBySsrCode(bundle.code).value.bundleSsrItems"
              :key="bundleItem.code"
            >
              <div class="ml-25">
                &bull; {{ bundleItem.name }}
              </div>
            </div>

            <template #footer>
              <div
                :class="`d-flex-between cursor-pointer px-50 py-50 ${isSelectedBundle(bundle) ? 'bg-light-success' : ''}`"
                @click="selectBundleHandle(bundle, trip.segments[0].tripId)"
              >
                <div class="">
                  <div
                    v-if="isSelectedBundle(bundle)"
                    class="fw-700 text-normal text-nowrap text-success"
                  >
                    {{ $t('flight.packageSelected') }}!
                  </div>
                  <div
                    v-else
                    class="fw-700 text-nowrap text-normal"
                  >
                    {{ $t('flight.selectPackagePaxTitle', {paxLen: passengerDataToAdd.filter(p => p.paxType !== 'INFANT')?.length}) }}
                  </div>
                </div>
                <div>
                  <feather-icon
                    v-if="isSelectedBundle(bundle)"
                    icon="XIcon"
                    size="24"
                    class="text-danger border-danger rounded-circle"
                  />
                  <feather-icon
                    v-else
                    icon="PlusIcon"
                    size="24"
                    class="text-primary border-warning rounded-circle"
                  />
                </div>
              </div>
            </template>
          </b-card>
        </AppCollapseItem>
      </AppCollapse>
    </IAmOverlay>
  </b-card>
</template>

<script>
import {
  BCard,
  BAlert,
  BTooltip,
} from 'bootstrap-vue'
import { computed } from '@vue/composition-api'
import { cloneDeep, isEmpty } from 'lodash-es'

import { getShortNameTripByMultiSegments } from '@/@core/utils/utils'
import store from '@/store'
import { formatCurrency } from '@/@core/utils/filter'
import { resolveSsrNameByCode } from '@/constants/selectOptions'

import useCreateBookingHandle from './useCreateBookingHandle'

export default {
  components: {
    BCard,
    BAlert,
    BTooltip,

    IAmOverlay: () => import('@/components/IAmOverlay.vue'),
    AppCollapse: () => import('@/@core/components/app-collapse/AppCollapse.vue'),
    AppCollapseItem: () => import('@/@core/components/app-collapse/AppCollapseItem.vue'),
  },
  props: {
    isHideFunction: {
      type: Boolean,
      default: false,
    },
    selectedTrip: {
      type: Array,
      required: true,
    },
    passengerDataToAdd: {
      type: Array,
      required: true,
    },
  },
  setup(props) {
    const {
      loadingAncillary,
      getSsrOthers,
      getAddonsData,
      bundleBySsrCode,
      toggleChangeBundle,
    } = useCreateBookingHandle()

    const tripDataLists = computed(() => {
      const results = cloneDeep(props.selectedTrip)
      const bundleLists = getSsrOthers.value.filter(ssr => !!bundleBySsrCode(ssr.code))
      if (!isEmpty(bundleLists)) {
        results.forEach(trip => {
          const flights = trip.segments.map(segment => `${segment.departure.IATACode}-${segment.arrival.IATACode}`).join(' | ')
          const bundleByTrips = bundleLists.filter(bundle => bundle.flights === flights).sort((a, b) => (a?.fares?.[0]?.total || 1) - (b?.fares?.[0]?.total || 0))
          if (!isEmpty(bundleByTrips)) {
            trip.bundles = bundleByTrips
          }
        })
      }
      return results
    })

    function isSelectedBundle(ssrBundle) {
      const selectedData = getAddonsData.value.filter(item => item.code === ssrBundle.code && item.flights === ssrBundle.flights)
      return !isEmpty(selectedData)
    }

    function getDataBundle(pax, ssr, tripId) {
      const data = {
        code: ssr.code,
        type: '',
        name: '',
        ...(ssr.details && {
          details: ssr.details.map(details => ({
            weight: String(details.weight),
            unit: details.unit,
          })),
        }),
        itineraryId: ssr.itineraryId ?? '',
        segmentIds: ssr.segmentIds ?? [],
        segmentId: ssr?.segmentIds?.join(' | '),
        paxId: pax.paxId,
        addonValue: ssr.ssrValue,
        paxType: pax.paxType,
        ssrName: ssr.ssrName,
        ssrId: ssr.ssrId,
        firstName: pax.firstName,
        lastName: pax.lastName,
        flights: ssr.flights,
        tripId,
        airline: ssr.airline,
        addonPrice: ssr.fares[0].total,
        amount: 1,
        serviceName: resolveSsrNameByCode(`${ssr.airline}_${ssr.code}`),
        source: ssr.source,
        isBundle: true,
      }
      return data
    }

    function selectBundleHandle(bundle, tripId) {
      const isSelectedBundle = getAddonsData.value.find(ssr => ssr.code === bundle.code && ssr.tripId === tripId && ssr.isBundle && ssr.flights === bundle.flights)
      if (isSelectedBundle) { // Delete
        const updatedData = getAddonsData.value.filter(ssr => !(ssr.tripId === tripId && ssr.isBundle && ssr.flights === bundle.flights))
        store.dispatch('app-create-booking/setAddonsSelectData', updatedData)
        toggleChangeBundle()
        return
      }

      const existDataSsr = getAddonsData.value.filter(ssr => !(ssr.tripId === tripId && ssr.isBundle && ssr.flights === bundle.flights) && !((ssr.ssrName === 'ssrBags') && (ssr.flights === bundle.flights)))

      // nếu chọn bundle > check nếu pax có bag => xoá hết bag đã thêm, warning cảnh báo đã xoá các gói

      const newBundleToAdd = props.passengerDataToAdd.filter(p => p.paxType !== 'INFANT').map(pax => getDataBundle(pax, bundle, tripId))
      const updatedData = [...existDataSsr, ...newBundleToAdd]
      store.dispatch('app-create-booking/setAddonsSelectData', updatedData)
      toggleChangeBundle()
    }

    return {
      loadingAncillary,
      tripDataLists,
      getAddonsData,

      isEmpty,
      isSelectedBundle,
      selectBundleHandle,
      formatCurrency,
      bundleBySsrCode,
      getShortNameTripByMultiSegments,
    }
  },
}
</script>
